import { useState, type FormEvent } from 'react';
import { api, ApiError, NetworkError } from '../lib/api';

type Phase = 'idle' | 'sending' | 'sent';

/**
 * Pantalla de login: pide el email y solicita un enlace mágico.
 * El enlace llega por correo y aterriza en /auth/callback?token=...
 */
export function Login() {
  const [email, setEmail] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setPhase('sending');
    setError(null);
    try {
      await api.post('/api/super-admin/auth/request-magic-link', { email: trimmed });
      setPhase('sent');
    } catch (err) {
      // Anti-oracle: el backend responde 200 aunque el email no exista, así
      // que un error aquí es de transporte o de validación de formato.
      if (err instanceof NetworkError) {
        setError('No se pudo conectar con el servidor. Intenta de nuevo.');
      } else if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Ocurrió un error inesperado.');
      }
      setPhase('idle');
    }
  }

  return (
    <div className="center-wrap">
      <div className="card card-narrow">
        <div className="eyebrow">Inventario · Super Admin</div>
        <h1 className="page-title mt-8">Iniciar sesión</h1>

        {phase === 'sent' ? (
          <>
            <div className="msg msg-ok mt-16">
              Revisa tu correo. Te enviamos un enlace de acceso a{' '}
              <strong>{email.trim()}</strong>. El enlace vence en unos minutos.
            </div>
            <p className="lead mt-16">
              ¿No llegó?{' '}
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '4px 10px', fontSize: 13 }}
                onClick={() => {
                  setPhase('idle');
                  setError(null);
                }}
              >
                Reenviar
              </button>
            </p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="lead mt-8">
              Ingresa tu correo de operador. Te enviaremos un enlace de acceso de
              un solo uso.
            </p>

            <div className="field mt-16">
              <label htmlFor="sa-email">Correo electrónico</label>
              <input
                id="sa-email"
                className="input"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={phase === 'sending'}
                placeholder="operador@empresa.com"
              />
            </div>

            {error && <div className="msg msg-error">{error}</div>}

            <button
              type="submit"
              className="btn btn-primary btn-block mt-16"
              disabled={phase === 'sending' || email.trim() === ''}
            >
              {phase === 'sending' ? 'Enviando…' : 'Enviar enlace de acceso'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
